import React, { useCallback, useEffect } from 'react';

import type { OnOpenChangeArgs } from '@atlaskit/dropdown-menu';
import { Section } from '@atlaskit/menu';
import { ButtonMenuItem, LinkMenuItem, MenuListItem } from '@atlaskit/navigation-system';
import {
	ExpandableMenuItem,
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/expandable-menu-item';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/flyout-menu-item';
import { fg } from '@atlaskit/platform-feature-flags';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import type {
	ButtonItemConfig,
	ExpandableItemConfig,
	LinkItemConfig,
	ObjectListFlyoutProps,
} from '@atlassian/navigation-common/types';
import {
	type ItemDragAndDropGroup,
	useL1ItemDragAndDrop,
} from '@atlassian/navigation-drag-and-drop';

import { useOptions } from '../../../controllers/options-provider';
import { ObjectListFlyout } from '../../../ui/object-list-flyout';
import { withSideNavigationAnalyticsListener } from '../../analytics/context';
import type { NavItemConfigInternal } from '../../types';
import {
	ElementEventListener,
	ElementEventListenerProvider,
} from '../../utils/element-event-listener';
import { omitSafely } from '../../utils/omit-safely';
import {
	isButtonItemConfig,
	isCustomItemConfig,
	isExpandableItemConfig,
	isFlyoutItemConfig,
	isLinkItemConfig,
	isObjectListFlyoutItemConfig,
} from '../../utils/type-guards';
import { useNavAnalytics } from '../../utils/use-nav-analytics';
import NavItemActions from '../nav-item-actions';

import { useExpandOpenState } from './use-expand-open-state';

export interface NavItemProps {
	group?: ItemDragAndDropGroup;
	id: string;
	isInMoreMenu?: boolean;
	item: NavItemConfigInternal;
	onClick?: () => void;
	// only used for custom items
	onPeek?: () => void;
	peek?: boolean;
}

export default function NavItem({
	id,
	item,
	peek = false,
	isInMoreMenu = false,
	onClick,
	onPeek,
	group = L1_MENU_GROUPS.MENU,
}: NavItemProps): React.ReactNode {
	const { l1DragAndDropEnabled } = useOptions();
	const { type } = item;

	const { isExpandOrFlyoutOpen, onExpandFlyoutToggle, isExpandable } = useExpandOpenState(
		item,
		peek,
	);
	const {
		draggableRef,
		isItemDraggable,
		isDragging,
		dropTargetRef,
		dropIndicator,
		maybeDragPreview,
	} = useL1ItemDragAndDrop({
		item,
		id,
		isExpandable,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		peek,
		group,
		l1DragAndDropEnabled,
	});

	const {
		fireClickedSideNavItemEvent,
		fireClickedSideNavSubMenuItemEvent,
		fireViewedSideNavSubMenuEvent,
	} = useNavAnalytics();

	// Common internal onClick handler for all top-level item types
	const onNavItemClick = useCallback(
		(isRightClick?: boolean) => {
			fireClickedSideNavItemEvent(id, undefined, isRightClick);
		},
		[fireClickedSideNavItemEvent, id],
	);

	// Common onSubMenuItemClick handler for all sub-menu item types
	const onSubMenuItemClick = useCallback(
		(isRightClick?: boolean) => {
			fireClickedSideNavSubMenuItemEvent(id, undefined, isInMoreMenu, isRightClick);
		},
		[fireClickedSideNavSubMenuItemEvent, id, isInMoreMenu],
	);

	useEffect(() => {
		if (isExpandable && isExpandOrFlyoutOpen) {
			fireViewedSideNavSubMenuEvent(id, undefined, isInMoreMenu);
		}
	}, [fireViewedSideNavSubMenuEvent, id, isExpandOrFlyoutOpen, isExpandable, isInMoreMenu, type]);

	if (isCustomItemConfig(item)) {
		const Wrapper = item.isMenuListItem ? React.Fragment : MenuListItem;

		// If the item is a custom item, render it directly
		// TODO: Add customizationHandlers prop when ready.
		return (
			<Wrapper>
				<item.Component
					draggableRef={draggableRef}
					dropIndicator={dropIndicator}
					hasDragIndicator={isItemDraggable}
					isDragging={isDragging}
					isExpandOrFlyoutOpen={isExpandOrFlyoutOpen}
					isInMoreMenu={isInMoreMenu && !peek}
					isPeeked={peek}
					onClick={(...args) => {
						item.Component.defaultProps?.onClick?.(...args);
						fireClickedSideNavItemEvent(id);
						onClick?.();
					}}
					onExpandFlyoutToggle={onExpandFlyoutToggle}
					onPeek={onPeek}
					visualContentRef={dropTargetRef}
				/>

				{maybeDragPreview}
			</Wrapper>
		);
	} else if (isLinkItemConfig(item)) {
		const { label, labelElem, ...linkMenuItemProps } = omitSafely(item, 'type');

		return (
			<ElementEventListener handler={onNavItemClick}>
				<LinkMenuItem
					{...linkMenuItemProps}
					onClick={(...args) => {
						linkMenuItemProps.onClick?.(...args);
						onNavItemClick();
						onClick?.();
					}}
					hasDragIndicator={isItemDraggable}
					ref={draggableRef}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={dropTargetRef}
					actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
				>
					{labelElem ?? label}
				</LinkMenuItem>
				{maybeDragPreview}
			</ElementEventListener>
		);
	} else if (isButtonItemConfig(item)) {
		const { label, labelElem, ...buttonMenuItemProps } = omitSafely(item, 'type');
		return (
			<>
				<ButtonMenuItem
					{...buttonMenuItemProps}
					onClick={(...args) => {
						buttonMenuItemProps.onClick?.(...args);
						onNavItemClick();
						onClick?.();
					}}
					hasDragIndicator={isItemDraggable}
					ref={draggableRef}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={dropTargetRef}
					actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
				>
					{labelElem ?? label}
				</ButtonMenuItem>
				{maybeDragPreview}
			</>
		);
	} else if (isExpandableItemConfig(item)) {
		const { children, elemBefore, label, labelElem, ...triggerProps } = omitSafely(
			item,
			'type',
			'isDefaultExpanded', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'isExpanded', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'onExpansionToggle', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
		);

		// Wrap children with our analytics listener to capture their click events
		const wrappedChildren = withSideNavigationAnalyticsListener(item.children, onSubMenuItemClick);

		return (
			<>
				<ExpandableMenuItem
					dropIndicator={dropIndicator}
					isExpanded={isExpandOrFlyoutOpen}
					onExpansionToggle={onExpandFlyoutToggle}
					ref={dropTargetRef}
				>
					<ExpandableMenuItemTrigger
						{...triggerProps}
						onClick={(...args) => {
							triggerProps.onClick?.(...args);
							onNavItemClick();
							onClick?.();
						}}
						// During drags, custom elemBefore is not shown
						// https://atlassian.design/components/navigation-system/side-nav-items/drag-and-drop#drag-start
						elemBefore={isDragging ? null : elemBefore}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
					>
						{labelElem ?? label}
					</ExpandableMenuItemTrigger>
					<ExpandableMenuItemContent>{wrappedChildren}</ExpandableMenuItemContent>
				</ExpandableMenuItem>
				{maybeDragPreview}
			</>
		);
	} else if (isFlyoutItemConfig(item)) {
		const { children, label, labelElem, ...triggerProps } = omitSafely(
			item,
			'type',
			'isDefaultOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'isOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'onOpenChange', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
		);

		// Wrap children with our analytics listener to capture their click events
		const wrappedChildren = withSideNavigationAnalyticsListener(item.children, onSubMenuItemClick);

		return (
			<>
				<FlyoutMenuItem isOpen={isExpandOrFlyoutOpen}>
					<FlyoutMenuItemTrigger
						{...triggerProps}
						onClick={(...args) => {
							triggerProps.onClick?.(...args);
							// Toggle the flyout open state
							onExpandFlyoutToggle(!isExpandOrFlyoutOpen);
							onNavItemClick();
							onClick?.();
						}}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					>
						{labelElem ?? label}
					</FlyoutMenuItemTrigger>
					<FlyoutMenuItemContent>
						{wrappedChildren}
						{isItemDraggable && !isInMoreMenu && (
							<Section hasSeparator>
								{fg('navx-1416-nav-item-actions-menu-refactor') ? (
									<NavItemActions
										itemMenuId={id}
										onOpenChange={(args: OnOpenChangeArgs) => {
											onExpandFlyoutToggle(args.isOpen);
										}}
									/>
								) : (
									<NavItemActions
										itemMenuId={id}
										renderFromLevel={1}
										onOpenChange={(args: OnOpenChangeArgs) => {
											onExpandFlyoutToggle(args.isOpen);
										}}
									/>
								)}
							</Section>
						)}
					</FlyoutMenuItemContent>
				</FlyoutMenuItem>
				{maybeDragPreview}
			</>
		);
	} else if (isObjectListFlyoutItemConfig(item)) {
		const objectListFlyoutProps = omitSafely(
			item,
			'type',
			'isOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in objectListFlyoutProps
			'onOpenChange', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in objectListFlyoutProps
		);

		return (
			<ElementEventListenerProvider handler={onSubMenuItemClick}>
				<ObjectListFlyout
					{...objectListFlyoutProps}
					itemMenuId={id}
					onClick={(...args) => {
						// Toggle the flyout open state
						onExpandFlyoutToggle(!isExpandOrFlyoutOpen);
						(objectListFlyoutProps as ObjectListFlyoutProps).onClick?.(...args);
						onNavItemClick();
						onClick?.();
					}}
					onSubMenuItemClick={(...args) => {
						(objectListFlyoutProps as ObjectListFlyoutProps).onSubMenuItemClick?.(...args);
						onSubMenuItemClick();
					}}
					isFlyoutOpen={isExpandOrFlyoutOpen}
					onOpenChange={onExpandFlyoutToggle}
					hasDragIndicator={isItemDraggable}
					triggerRef={draggableRef}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={dropTargetRef}
				/>
				{maybeDragPreview}
			</ElementEventListenerProvider>
		);
	} else {
		return null;
	}
}

type ActionsOnHoverProps = {
	group?: ItemDragAndDropGroup;
	id: string;
	isInMoreMenu?: boolean;
	isItemDraggable: boolean;
	item: ButtonItemConfig | ExpandableItemConfig | LinkItemConfig;
};

// This cannot be a React function-component, otherwise the Link/Button/Expand.actionsOnHover prop will can never be undefined.
// When there are no actionsOnHover and the item is not draggable, actionsOnHover MUST return undefined, to avoid the
// elemAfter from being hidden unintentionally.
function getActionsOnHover({
	id,
	item,
	isItemDraggable,
	isInMoreMenu,
	group,
}: ActionsOnHoverProps) {
	const shouldShowNavItemActions = isItemDraggable && !isInMoreMenu;

	return item.actionsOnHover ? (
		<>
			{item.actionsOnHover}
			{shouldShowNavItemActions && <NavItemActions itemMenuId={id} group={group} />}
		</>
	) : shouldShowNavItemActions ? (
		<NavItemActions itemMenuId={id} group={group} />
	) : undefined;
}
